import React, { createContext, useContext, useMemo } from 'react';
import {
  serverTimestamp, getDocsFromServer, query, where, limit as limitTo,
} from 'firebase/firestore';
import { writeBatch, writeQueue } from './writes';
import { queuedFamilyMentions } from './queuedHistory';
import { col, docRef, COLLECTIONS } from './paths';
import { db } from '../lib/firebase';
import { useAuth } from '../context/AuthContext';
import { freeId } from '../domain/freeId';
import {
  normaliseFamilyContact, familyContacts, familyBillingContacts, planFamilyDeletion,
} from '../domain/people';
import { useCollectionWindow } from './useCollectionWindow';

// Families — the billing entity. Deliberately holds no balance field; balances
// are folded from the ledger (see useLedger and core/src/ledger.rs).

const FamiliesContext = createContext(null);
const TRACK_LIMIT = 2000;

// The contacts window. Wider than the families one because a family has several
// people on it (#176): 4,000 is two per family at the families limit. A studio
// past it loses the *later* contacts of the families sorted last.
//
// One window rather than a subscription per family: a per-family listener would
// open and close as a teacher clicks down a list.
const CONTACTS_LIMIT = 4000;

// How many contacts one family's delete will sweep. A `writeBatch` takes 500
// writes and the family document is one of them, so 400 leaves room and stays
// one batch, so the cascade cannot half-apply. A family with more contacts
// than this cannot be deleted at all (see the read in `removeFamily`).
const CASCADE_LIMIT = 400;

// ── What holds a family back from being deleted (#346) ──────────────────────
//
// Like `STUDENT_REFERENCES` in useStudents.jsx: **every collection that stores
// a family's id belongs here**, and `planFamilyDeletion`'s claim that nothing
// is orphaned is a claim about this array.
//
// Ordered most-likely-first, because `probeFamilyReferences` short-circuits:
// an empty family pays for all eight, a family with students one round trip.
//
// **`label` is copy**, shown in the refusal a teacher reads.
// `familyDeletionRefusal` in src/domain/people.js assembles the sentence.
//
// **`invoiceRequests`** is a queued request the invoice function has not run
// yet, naming this family in a `familyIds` array; deleting the family would
// leave the server about to invoice a document that is gone.
//
// **`messages`** carries its family at `context.familyId`. A dotted field path
// is a legal `where` and Firestore indexes nested fields by default;
// `firestore.indexes.json` has no `fieldOverrides` exempting it. It is the one
// member `queuedFamilyMentions` cannot see, for the reason given there.
//
// **`familyContacts` is not here**: a contact is the family's own people, not a
// record of something that happened (#320's argument), so the contacts cascade
// — `removeFamily` deletes them in the same batch — instead of blocking.
export const FAMILY_REFERENCES = [
  { collection: COLLECTIONS.students, field: 'familyId', op: '==', label: 'students' },
  { collection: COLLECTIONS.ledgerEntries, field: 'familyId', op: '==', label: 'transactions' },
  { collection: COLLECTIONS.invoices, field: 'familyId', op: '==', label: 'invoices' },
  { collection: COLLECTIONS.attendance, field: 'familyId', op: '==', label: 'lesson history' },
  { collection: COLLECTIONS.packageEntries, field: 'familyId', op: '==', label: 'packages' },
  { collection: COLLECTIONS.makeupEntries, field: 'familyId', op: '==', label: 'make-up credits' },
  {
    collection: COLLECTIONS.invoiceRequests,
    field: 'familyIds',
    op: 'array-contains',
    label: 'an invoice waiting to be generated',
  },
  {
    collection: COLLECTIONS.messages,
    field: 'context.familyId',
    op: '==',
    label: 'messages',
  },
];

/**
 * Which of `FAMILY_REFERENCES` hold this family, as `{ label, found }` per
 * entry, where `found` is `true` / `false` / **`null` for could-not-tell**.
 *
 * The mechanism is `probeStudentHistory`'s (see `STUDENT_REFERENCES` in
 * useStudents.jsx): a `limit(1)` asked of the *server* cannot be truncated
 * into a false negative, where a client window that hit its bound is
 * indistinguishable from a complete one. `getDocsFromServer` rejects rather
 * than falling back to the SDK cache, so offline every answer is `null` and
 * `planFamilyDeletion` refuses.
 *
 * **It stops at the first hit**, so the refusal names the first thing found
 * rather than everything.
 */
async function probeFamilyReferences(uid, familyId, queued) {
  // The queue first, and only ever as a yes, as `probeSelection` in
  // useStudents.jsx takes it: a mention there is a write this device has made
  // and the server has not answered for. Its silence is never a no, so the
  // loop below still runs.
  //
  // **Its own label**, rather than a collection's: the queue is walked across
  // every collection at once, so it knows the family is mentioned and not
  // which of the eight mentioned it.
  if (queued === null) return [{ label: 'writes this device has not sent yet', found: null }];
  if (queued.has(familyId)) {
    return [{ label: 'a write this device has not sent yet', found: true }];
  }

  const answers = [];
  for (const ref of FAMILY_REFERENCES) {
    let snap;
    try {
      snap = await getDocsFromServer(query(
        col(uid, ref.collection),
        where(ref.field, ref.op, familyId),
        limitTo(1),
      ));
    } catch (err) {
      // For this query's own failure and nothing else — the `await` is the only
      // statement in the `try` (#184). Offline, a refused read and a malformed
      // query all land here; all this knows is that the collection did not
      // answer.
      console.error(
        `[families] ${ref.collection} did not answer whether ${familyId} is referenced; `
        + 'treating it as unknown, which refuses the delete:',
        err,
      );
      answers.push({ label: ref.label, found: null });
      return answers;
    }
    answers.push({ label: ref.label, found: !snap.empty });
    if (!snap.empty) return answers;
  }
  return answers;
}

export function FamiliesProvider({ children }) {
  const { user } = useAuth();

  // The families window, from whichever read path serves this session (#207
  // U5). `withMetadata` keeps the snapshot path's `includeMetadataChanges`;
  // `pendingWrite` is spread by the default row.
  const read = useCollectionWindow({
    uid: user?.uid,
    collection: COLLECTIONS.families,
    limit: TRACK_LIMIT,
    withMetadata: true,
  });
  const families = read.docs;
  const loading = read.loading;

  // The people on those families, beyond the one each family document carries
  // inline (#176). A flat collection with a `familyId`; the reasoning for flat
  // rather than a subcollection is at `normaliseFamilyContact` in
  // src/domain/people.js.
  const contactsRead = useCollectionWindow({
    uid: user?.uid,
    collection: COLLECTIONS.familyContacts,
    limit: CONTACTS_LIMIT,
  });
  const contacts = contactsRead.docs;

  // ── One crossing per read, rather than one per row (#394, #401) ────────────
  //
  // `contactsFor` and `billingContactFor` below hand the **whole** contacts
  // window to the core — up to `CONTACTS_LIMIT` records, serialised across the
  // wasm boundary — and `PackageBalancesPanel` asks the second of them once per
  // student row. Nearly all of a call's cost is the crossing itself (several
  // milliseconds at 4,000 contacts), so nothing on this side of the boundary
  // reduces it.
  //
  // So `familyBillingContacts` in src/domain/people.js crosses **once** and
  // comes back with every family's answer. That one call costs more than a
  // single-family one, since it groups and sorts every contact, and replaces
  // one per row.
  //
  // Built lazily rather than in a `useMemo`, because most screens never ask,
  // and resolving every family's recipient on every contacts read would charge
  // them for it.
  //
  // `billing.map` is `null` until the first `billingContactFor`, and the whole
  // holder is rebuilt when `contacts` changes — `docs` is itself memoised in
  // `useCollectionWindow`, so it is a new array only when the data moved.
  //
  // **A family absent from the map is a family with no contacts**, which is
  // `familyBillingContact`'s own `null`; the two agree for every id, asserted
  // in `core/src/names.rs` and in `test/core/familyContacts.wasm.test.js`.
  //
  // `contactsFor` keeps a per-family cache instead: it answers with a *list*
  // per family, so one call for all would cross the whole studio's ordering to
  // serve one card. `FamiliesPage` calls it once per family it draws.
  const contactsCache = useMemo(() => new Map(), [contacts]);
  const cached = (cache, key, compute) => {
    if (!cache.has(key)) cache.set(key, compute());
    return cache.get(key);
  };
  const billing = useMemo(() => ({ map: null }), [contacts]);
  const billingFor = (familyId) => {
    if (!familyId) return null;
    if (billing.map === null) billing.map = familyBillingContacts(contacts);
    return billing.map.get(familyId) ?? null;
  };

  const value = useMemo(() => ({
    loading,
    // The read stopped before it answered (#647) — src/domain/reading.js.
    failed: read.failed,
    families,
    byId: (id) => families.find((f) => f.id === id) || null,
    updateFamily: async (id, patch) => {
      if (!user) return;
      const batch = writeBatch(db);
      batch.set(docRef(user.uid, COLLECTIONS.families, id),
                { ...patch, updatedAt: serverTimestamp() }, { merge: true });
      await batch.commit();
    },

    // ── The contacts half ────────────────────────────────────────────────
    contacts,
    contactsLoading: contactsRead.loading,
    contactsFailed: contactsRead.failed,
    // Sorted, and the sort is the domain's rather than an `orderBy`: this is a
    // filter over one window, so the ordering has to be applied after the
    // filter anyway and doing it in one place keeps the tab and a test agreeing.
    contactsFor: (familyId) => cached(
      contactsCache, familyId ?? null, () => familyContacts(contacts, familyId),
    ),

    // Which contact wears the `Preferred Invoice Recipient` badge (#413).
    //
    // It is not who this family's receipts, notices and reminders are
    // addressed to (#319): that audience is every contact, BCC'ed into one
    // message, so those callers ask `contactsFor` above (see
    // `familyRecipients` in src/domain/notifications.js). The callers here are
    // the badge on a contact card (`FamilyContactsTab`, `FamilyDetail`), the
    // name on the account card and in `FamilyList` and `PackageBalancesPanel`,
    // and the Send Invoice drawer's default To (`useSendInvoice.js`, a
    // single-select, #405). MMS badges exactly one contact.
    //
    // One grouping in `core/src/names.rs` — `family_billing_contacts` is
    // `family_contacts_by_family` plus `pick_recipient` — so the tie-break for
    // a family that has flagged two contacts (`familyBillingContact` in
    // src/domain/people.js) is made in one place. The server resolves the
    // greeting from the same function: `functions/delivery/facts.js` hands one
    // `billingContact` to every template.
    //
    // Null while `contactsLoading` is true, and for a family with no contacts.
    //
    // **This collection is `CLASS_PER_FIELD_MERGE`** in `core/src/reconcile.rs`
    // (#391), because an offline edit to a contact is an offline edit to a
    // studio's billing address. `saveContact` below sends the patch that class
    // needs in order to fold anything.
    //
    // Answered for every family in one crossing (#401) — see `billingFor`
    // above the memo.
    billingContactFor: billingFor,

    // Create and edit are one call, as they are for a tag (#138) and a
    // category: one drawer adds and edits.
    //
    // **An edit is not a whole-document write (#391).** The form carries every
    // field, and `normaliseFamilyContact` returns null for an empty field so
    // that "the teacher deleted this" is writable. A per-field fold over a
    // payload naming every field is whole-document last-write-wins: two devices
    // editing two *different* fields of one contact offline would lose one
    // change (#198 on `tags`, #337 on `settings`). So the edit names the fields
    // that **changed**; a cleared field is a changed field and rides along as an
    // explicit `null`. `saveStudio` in useSettings.jsx has the same shape.
    //
    // **The base is the row the drawer opened on (#396), and the caller hands
    // it over** — not this provider's live window, which can be fresher than the
    // value the form was seeded from:
    //
    //   1. the teacher opens the drawer, seeded from the row as it is now;
    //   2. another device changes the billing address, and it arrives through
    //      the window while the drawer is open;
    //   3. the teacher changes the email and saves.
    //
    // Diffed against the window, the stale address in the form would differ
    // from the new one and revert the other device's change; against the
    // drawer's base it is unchanged and stays out of the patch. The student
    // drawer seeds `baseTagIds` beside `tagIds` for the same reason.
    //
    // **`base` is required on an edit, and its absence throws** (#396).
    // `base: null` means the caller has no base, and the whole record goes.
    // The one production call site, `StudentDetailPage`, reaches this branch
    // only with the draft in hand, so the throw is a programming error.
    //
    // The create writes whole. Two `batch.set` calls rather than one with a
    // computed `merge`, because the static scan in
    // test/core/wholeDocumentSets.test.js reads `merge: true` literally.
    saveContact: async ({ id = null, familyId, ...fields }) => {
      if (!user || !familyId) return null;
      // `base` out of `fields` by hand rather than in the destructuring above,
      // so that "the key was not passed" survives to be tested for. A
      // destructured `base` is `undefined` either way.
      const hasBase = Object.prototype.hasOwnProperty.call(fields, 'base');
      const { base, ...formFields } = fields;
      const record = normaliseFamilyContact({ ...formFields, familyId });

      if (id) {
        if (!hasBase) {
          throw new Error(
            'saveContact: an edit needs `base`, the contact row the drawer was seeded from. '
            + 'Pass `base: null` to send the whole record when there is genuinely no base (#396).',
          );
        }
        // Keys of `record`, not of `base`: `createdAt` and `updatedAt` are on
        // the row and are not fields this form owns, and re-sending `createdAt`
        // is what `createdAtUnchanged()` in firestore.rules refuses.
        const patch = base
          ? Object.fromEntries(
            Object.keys(record).filter((k) => record[k] !== base[k]).map((k) => [k, record[k]]),
          )
          : record;
        const batch = writeBatch(db);
        batch.set(docRef(user.uid, COLLECTIONS.familyContacts, id),
                  { ...patch, updatedAt: serverTimestamp() }, { merge: true });
        await batch.commit();
        return id;
      }

      const contactId = freeId('fct');
      const batch = writeBatch(db);
      batch.set(docRef(user.uid, COLLECTIONS.familyContacts, contactId), {
        ...record,
        updatedAt: serverTimestamp(),
        // Only on a create, for the rule named above.
        createdAt: serverTimestamp(),
      });
      await batch.commit();
      return contactId;
    },

    // ── Removing the family itself (#346) ──────────────────────────────────
    //
    // The student delete leaves the family alone (`planStudentDeletion` in
    // src/domain/people.js), so this removes an empty account.
    //
    // Two outcomes rather than the student delete's three: a family has no
    // status to be retired into (`planFamilyDeletion`).
    //
    // Returns the plan — `{ deletable, held, unknown }` — whether or not it
    // deleted anything, so a caller renders the refusal from the same value it
    // used to decide. It never throws for a refusal; a rejected *write* still
    // rejects.
    //
    // **The contacts go with it, in the same batch** (as `planTagDetachment`
    // is for students), so no `familyContacts` row is left naming nothing. One
    // batch, so it cannot half-apply; offline the probe refuses first.
    removeFamily: async (id) => {
      const empty = { deletable: false, held: [], unknown: [] };
      if (!user || !id) return empty;

      let queued;
      try {
        queued = queuedFamilyMentions(
          await writeQueue().records(), user.uid,
          FAMILY_REFERENCES.map((r) => r.collection),
        );
      } catch (err) {
        // `records()`' own failure and nothing else: it is the only statement
        // in the `try` that can throw for a reason of its own (#184).
        console.error(
          '[families] the write queue did not say what this device has written but not sent; '
          + 'treating the family as unknown, which refuses the delete:',
          err,
        );
        queued = null;
      }

      const answers = await probeFamilyReferences(user.uid, id, queued);
      // The contacts cascade rather than block, but *finding* them is asked of
      // the server for the same reason: a window that hit its bound is
      // indistinguishable from a complete one (#318), so deleting from it could
      // leave a contact behind pointing at nothing.
      let doomed = null;
      if (planFamilyDeletion(answers).deletable) {
        try {
          const snap = await getDocsFromServer(query(
            col(user.uid, COLLECTIONS.familyContacts),
            where('familyId', '==', id),
            limitTo(CASCADE_LIMIT),
          ));
          doomed = snap.docs.map((d) => d.id);
        } catch (err) {
          console.error(
            '[families] familyContacts did not say who is on this family; '
            + 'treating it as unknown, which refuses the delete:',
            err,
          );
        }
        // At the bound, this cannot say there are no more, so it refuses
        // instead of deleting the ones it can see.
        if (doomed && doomed.length >= CASCADE_LIMIT) doomed = null;
        answers.push({ label: 'contacts', found: doomed === null ? null : false });
      }

      const plan = planFamilyDeletion(answers);
      if (!plan.deletable) return plan;

      const batch = writeBatch(db);
      for (const contactId of doomed) {
        batch.delete(docRef(user.uid, COLLECTIONS.familyContacts, contactId));
      }
      batch.delete(docRef(user.uid, COLLECTIONS.families, id));
      await batch.commit();
      return plan;
    },

    // Ungated, as in MMS. Nothing else references a contact, so there is
    // nothing to clean up.
    removeContact: async (id) => {
      if (!user || !id) return;
      const batch = writeBatch(db);
      batch.delete(docRef(user.uid, COLLECTIONS.familyContacts, id));
      await batch.commit();
    },
    // The two caches are keyed to `contacts` by their own memos, so naming them
    // here is not what keeps them fresh — it is what stops this memo handing
    // out a closure over a cache that has already been replaced.
  }), [families, loading, read.failed, user, contacts, contactsRead.loading, contactsRead.failed, contactsCache, billing]);

  return <FamiliesContext.Provider value={value}>{children}</FamiliesContext.Provider>;
}

export function useFamilies() {
  const ctx = useContext(FamiliesContext);
  if (!ctx) throw new Error('useFamilies must be used within a FamiliesProvider');
  return ctx;
}
